// Navigation component with Back/Next buttons

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface WizardNavigationProps {
  onNext: () => void;
  onBack: () => void;
  onSubmit?: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  isLoading?: boolean;
  isValid?: boolean;
  nextLabel?: string;
  backLabel?: string;
  submitLabel?: string;
  className?: string;
}

export function WizardNavigation({
  onNext,
  onBack,
  onSubmit,
  isFirstStep,
  isLastStep,
  isLoading = false,
  isValid = true,
  nextLabel = "Next",
  backLabel = "Back",
  submitLabel = "Create Book",
  className
}: WizardNavigationProps) {
  const handleNext = () => {
    if (isLastStep && onSubmit) {
      onSubmit();
    } else {
      onNext();
    }
  };

  return (
    <div className={cn("flex justify-between items-center", className)}>
      {/* Back button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isFirstStep || isLoading}
          className={cn(
            "transition-all duration-200",
            isFirstStep && "invisible"
          )}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {backLabel}
        </Button>
      </motion.div>

      {/* Next/Submit button */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Button
          type="button"
          onClick={handleNext}
          disabled={!isValid || isLoading}
          className={cn(
            "px-8 py-2 text-white font-medium shadow-lg transform transition-all duration-200",
            "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700",
            "hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none",
            isLastStep && "px-10"
          )}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              {isLastStep ? submitLabel : nextLabel}
              {!isLastStep && <ArrowRight className="w-4 h-4 ml-2" />}
            </>
          )}
        </Button>
      </motion.div>
    </div>
  );
}

// Mobile-optimized navigation variant
export function MobileWizardNavigation(props: WizardNavigationProps) {
  return (
    <WizardNavigation
      {...props}
      className={cn(
        "fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 z-40",
        "sm:relative sm:bottom-auto sm:left-auto sm:right-auto sm:bg-transparent sm:border-0 sm:px-0 sm:py-0",
        props.className
      )}
    />
  );
}
